import ParcelProvision from '../parcels/ParcelProvision';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { JobDetail as JobDetailType } from '../shared/api';
import { COUNTY_LABELS } from '../shared/api';
import { Banner, Card, Pill, Spinner, StatusBadge } from '../components/ui';
import { api } from '../lib/api';
import { daysLabel, daysUntil, fmtDate, money, ROLE_LABELS, toneForDeadline, TONE_TEXT } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import DocumentsTab from './job/DocumentsTab';
import FilingTab from './job/FilingTab';
import InvoicesTab from './job/InvoicesTab';
import MailingTab from './job/MailingTab';
import NocTab, { NocStatusBadge } from './job/NocTab';
import NooTab from './job/NooTab';
import OverviewTab from './job/OverviewTab';
import ReferralTab from './job/ReferralTab';
import SuppliersTab from './job/SuppliersTab';
import WaiversTab from './job/WaiversTab';
import WorkLogTab from './job/WorkLogTab';
import JobActions from './job/JobActions';

const TABS = [
  { key: 'overview', label: 'Overview', render: OverviewTab },
  { key: 'noo', label: 'NTO', render: NooTab },
  // Self-service paths: the contractor does the mailing and the notarizing.
  { key: 'mailing', label: 'Self-Mail', render: MailingTab },
  { key: 'noc', label: 'NOC', render: NocTab },
  { key: 'documents', label: 'Documents', render: DocumentsTab },
  { key: 'invoices', label: 'Invoices', render: InvoicesTab },
  { key: 'suppliers', label: 'Suppliers', render: SuppliersTab },
  { key: 'waivers', label: 'Waivers', render: WaiversTab },
  { key: 'work', label: 'Work Log', render: WorkLogTab },
  { key: 'filing', label: 'Filing', render: FilingTab },
  { key: 'referral', label: 'Counsel', render: ReferralTab },
] as const;

function Deadline({ label, iso, done, doneLabel }: { label: string; iso: string | null; done?: boolean; doneLabel?: string }) {
  const days = daysUntil(iso);
  const tone = done ? 'ok' : toneForDeadline(iso);
  return (
    <div className="flex-1 rounded-lg border border-border bg-surface px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-dim">{label}</p>
      <p className={`mt-1 text-sm font-semibold ${TONE_TEXT[tone]}`}>{fmtDate(iso)}</p>
      <div className="mt-1.5">
        <Pill tone={tone}>{done ? (doneLabel ?? 'Done') : daysLabel(days)}</Pill>
      </div>
    </div>
  );
}

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('overview');
  const { data, loading, error, reload } = useAsync(
    () => api.get<{ job: JobDetailType }>(`/api/jobs/${id}`),
    [id],
  );

  if (loading && !data) return <Spinner label="Loading job…" />;
  if (error) return <Banner tone="urgent" title="Could not load this job">{error}</Banner>;
  const job = data?.job;
  if (!job) return <Banner tone="urgent">Job not found.</Banner>;

  const Active = TABS.find((t) => t.key === tab)!.render;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/jobs" className="text-xs font-semibold text-muted hover:text-ink">
          ← All jobs
        </Link>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="text-xl font-bold text-ink">{job.propertyAddress}</h1>
          <StatusBadge status={job.status} />
          <Pill>{ROLE_LABELS[job.contractorRole]}</Pill>
          <Pill tone={job.paVerified ? 'ok' : 'watch'}>
            {job.paVerified ? 'PA verified' : 'Not PA verified'}
          </Pill>
          {job.ownerMismatch && <Pill tone="watch">Owner ≠ who hired us</Pill>}
          {job.contractorRole === 'prime' && job.nocStatus && <NocStatusBadge status={job.nocStatus} />}
        </div>
        <p className="mt-1 text-sm text-muted">
          {COUNTY_LABELS[job.county]} County · Contracted with {job.contractedWithName} · {money(job.contractAmount)}
        </p>
      </div>

      {job.archivedAt && (
        <Banner tone="watch" title="Archived job">
          Kept for your records. It is off your job list and does not count toward the pilot’s job limit.
        </Banner>
      )}

      <ParcelProvision source={{ ...job, kind: 'job' }} />

      {job.selfServiceDisclaimer && (
        <Banner tone="watch" title="Service handled outside LienGuardian">
          This notice was mailed by the contractor, so LienGuardian did not create the service evidence and cannot
          guarantee service or any other aspect of compliance for this job.
        </Banner>
      )}

      {job.deliveryProofWarning && (
        <Banner tone="watch" title="Keep the delivery paper trail">
          {job.deliveryProofWarning}
        </Banner>
      )}

      {job.fabricationReviewWarning && (
        <Banner tone="watch" title="Made for this job, not delivered yet">
          {job.fabricationReviewWarning}
        </Banner>
      )}

      {job.currentAlert && (
        <Banner tone={job.currentAlert.level === 'passed' ? 'urgent' : 'soon'} title={job.currentAlert.title}>
          {job.currentAlert.body}
        </Banner>
      )}

      <div className="flex flex-col gap-3 sm:flex-row">
        <Deadline label="Notice to Owner" iso={job.nooDeadline} done={!!job.nooSentAt} doneLabel="Sent" />
        <Deadline label="Claim of Lien" iso={job.lienDeadline} done={job.lienFiled} doneLabel="Filed" />
        <Deadline label="Lien enforcement" iso={job.lienEnforcementDeadline} />
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-border" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3.5 py-2 text-sm font-medium transition-colors ${
              tab === t.key ? 'border-tint text-ink' : 'border-transparent text-muted hover:text-ink'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <Card>
        <Active job={job} reload={reload} goToTab={(key) => setTab(key as (typeof TABS)[number]['key'])} />
      </Card>

      <JobActions job={job} reload={reload} />
    </div>
  );
}
